// El contrato solo guarda `montoActual`, asi que al aplicar un aumento las
// pantallas por periodo (Cobros, Liquidacion, Reportes) pasaban a mostrar el
// monto nuevo tambien en los meses anteriores: se perdia lo que realmente se
// cobro. Este modulo arma la linea de tiempo de montos de un contrato y
// responde "cuanto valia en tal mes".
//
// Fuentes, de mas a menos confiable:
//   1. El pago del mes (guarda `montoEsperado`, lo que se cobro de verdad).
//   2. `contrato.historialMontos`, que se escribe al aplicar cada aumento.
//   3. Los comprobantes de `alquileres_aumentos_aplicados` (montoAnterior /
//      montoNuevo + fechaAumento), que permiten reconstruir el historial de los
//      contratos anteriores a esta funcionalidad sin migrar nada.
//   4. `montoActual` como ultimo recurso.

export const mesKey = (anio, mes) => Number(anio) * 12 + (Number(mes) - 1);

export const mesKeyDesdeISO = (iso) => {
  if (!iso) return null;
  const [y, m] = String(iso).split("-").map(Number);
  if (!y || !m) return null;
  return mesKey(y, m);
};

const norm = (t) => ({ ...t, monto: Number(t.monto) || 0 });

// Une los tramos reconstruidos desde los comprobantes con los guardados en el
// contrato: para una misma fecha manda el guardado (es el dato confirmado), y
// los del comprobante cubren los periodos que el historial guardado no alcanza
// (por ejemplo cuando arranca recien en el ultimo aumento).
const combinar = (deComprobantes, deHistorial) => {
  const porFecha = new Map();
  [...deComprobantes, ...deHistorial].forEach((t) => porFecha.set(t.desdeKey, t));
  return [...porFecha.values()].sort((a, b) => a.desdeKey - b.desdeKey).map(norm);
};

// Devuelve los tramos de precio ordenados por fecha: [{ desdeKey, monto, origen }].
// El primer tramo se considera vigente hacia atras (no sabemos nada anterior).
export const tramosMonto = (contrato, aumentosAplicados = []) => {
  if (!contrato) return [];

  const guardado = (Array.isArray(contrato.historialMontos) ? contrato.historialMontos : [])
    .map((t) => ({ desdeKey: mesKeyDesdeISO(t.desde), monto: Number(t.monto) || 0, origen: "historial" }))
    .filter((t) => t.desdeKey != null && t.monto > 0);

  // Reconstruccion desde los comprobantes de aumento ya guardados.
  const propios = (aumentosAplicados || [])
    .filter((a) => a.contratoId === contrato.idFirebase && a.fechaAumento)
    .sort(
      (a, b) =>
        String(a.fechaAumento).localeCompare(String(b.fechaAumento)) ||
        String(a.createdAt || "").localeCompare(String(b.createdAt || ""))
    );
  if (!propios.length) return combinar([], guardado);

  // Un mismo aumento puede tener varios comprobantes en la misma fecha: por un
  // doble clic (identicos) o porque se aplico dos veces encadenado
  // (100->110 y 110->120). Se colapsan en un solo tramo: el monto de entrada es
  // el del comprobante mas viejo y el de salida el del mas nuevo.
  const porFecha = new Map();
  propios.forEach((a) => {
    const previo = porFecha.get(a.fechaAumento);
    const montoNuevo = Number(a.montoNuevo) || 0;
    if (!previo) {
      porFecha.set(a.fechaAumento, { montoAnterior: Number(a.montoAnterior) || 0, montoNuevo });
    } else if (montoNuevo > 0) {
      porFecha.set(a.fechaAumento, { ...previo, montoNuevo });
    }
  });
  const ordenados = [...porFecha.entries()]
    .map(([fechaAumento, v]) => ({ fechaAumento, ...v }))
    .sort((a, b) => String(a.fechaAumento).localeCompare(String(b.fechaAumento)));

  const tramos = [];
  const primero = ordenados[0];
  if (primero.montoAnterior > 0) {
    tramos.push({ desdeKey: -Infinity, monto: primero.montoAnterior, origen: "comprobante" });
  }
  ordenados.forEach((a) => {
    const desdeKey = mesKeyDesdeISO(a.fechaAumento);
    if (desdeKey == null || !(a.montoNuevo > 0)) return;
    tramos.push({ desdeKey, monto: a.montoNuevo, origen: "comprobante" });
  });
  return combinar(tramos, guardado);
};

// Monto vigente del contrato en un mes/anio dado.
// Devuelve { monto, esHistorico, origen }. `esHistorico` es true cuando el monto
// del periodo no coincide con el monto actual del contrato (sirve para avisar en
// la UI que se esta mostrando un precio viejo).
export const montoEnPeriodo = (contrato, anio, mes, opts = {}) => {
  const actual = Number(contrato?.montoActual) || 0;
  const { aumentosAplicados = [], pago = null } = opts;

  const esperado = Number(pago?.montoEsperado) || 0;
  if (esperado > 0) {
    return { monto: esperado, esHistorico: esperado !== actual, origen: "pago" };
  }

  const tramos = tramosMonto(contrato, aumentosAplicados);
  if (!tramos.length) return { monto: actual, esHistorico: false, origen: "contrato" };

  const k = mesKey(anio, mes);
  let elegido = tramos[0];
  for (const t of tramos) {
    if (t.desdeKey <= k) elegido = t;
    else break;
  }
  const monto = elegido.monto > 0 ? elegido.monto : actual;
  return { monto, esHistorico: monto !== actual, origen: elegido.origen };
};

// Corrige el monto del tramo vigente en `fechaRef` (hoy por defecto) sin crear
// un tramo nuevo: sirve para arreglar un monto mal cargado, que no es un cambio
// de precio y por lo tanto no tiene que partir el historial en dos.
// Devuelve el historial corregido, o null si el contrato todavia no tiene.
export const corregirTramoVigente = (contrato, monto, fechaRef) => {
  const previo = Array.isArray(contrato?.historialMontos) ? contrato.historialMontos : [];
  if (!previo.length) return null;

  const hoy = new Date();
  const ref = fechaRef ? mesKeyDesdeISO(fechaRef) : mesKey(hoy.getFullYear(), hoy.getMonth() + 1);
  const ordenado = [...previo].sort((a, b) => String(a.desde).localeCompare(String(b.desde)));

  let idx = 0;
  ordenado.forEach((t, i) => {
    const k = mesKeyDesdeISO(t.desde);
    if (k != null && ref != null && k <= ref) idx = i;
  });

  return ordenado.map((t, i) =>
    i === idx
      ? { ...t, monto: Number(monto) || 0, corregidoEn: new Date().toISOString() }
      : t
  );
};

// Agrega el tramo del aumento recien aplicado al historial del contrato,
// sembrando el tramo inicial cuando el contrato todavia no tenia historial.
export const agregarTramoMonto = (contrato, tramo) => {
  const previo = Array.isArray(contrato?.historialMontos) ? contrato.historialMontos : [];
  const base = previo.length
    ? previo
    : (() => {
        const desde =
          contrato?.aumento?.fechaUltimoAumento || contrato?.fechaInicio || "";
        const monto = Number(contrato?.montoActual) || 0;
        return desde && monto > 0
          ? [{ desde, monto, motivo: "INICIAL", registradoEn: new Date().toISOString() }]
          : [];
      })();

  return [...base.filter((t) => t.desde !== tramo.desde), tramo].sort((a, b) =>
    String(a.desde).localeCompare(String(b.desde))
  );
};
